import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { MdCloudUpload, MdCancel, MdCheck } from "react-icons/md";
import { read, utils } from "xlsx";
import { useToast } from "../../hooks/useToast";
import { LOCATIONS_URL } from "../../constants/urls";
import { ENTITY } from "../../constants/entities";

import type { Store, Location, ExcelCellProps } from "../../types";
import { normalizeExcel, normalizeHeader, normalizeSchemaKeys } from "helpers";
import { useSession } from "providers/SessionProvider";
import useCrud from "hooks/useCrud";
import { LocationTypes } from "constants/index";
import { validateSchema } from "helpers/schemaValidator";
import { STATUS_CODES } from "constants/index";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { toPositiveNumber } from "helpers/formatNumber";

type Props = {
  stores: Store[];
};

type NewLocationRow = Record<string, any>;
type NewLocationPayload = Pick<
  Location,
  "code" | "store_id" | "location_type" | "width" | "height" | "long"
>;

const SHEET_NAME = "UBICACIONES";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function FileImportLocations({ stores }: Props) {
  const [uploadedLocations, setUploadedLocations] = useState<
    NewLocationPayload[]
  >([]);
  const [listOfErrors, setListOfErrors] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetInput, setResetInput] = useState(Math.random().toString(36));
  const [error, setError] = useState(false);
  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const { addSessionData, updateSessionData } = useSession();
  const { showSnackBar } = useToast();

  const { createData: createOrUpdateLocationsMassive } = useCrud<any>(
    `${LOCATIONS_URL}/massive`,
    { disableAutoFetch: true },
  );

  const storeAssociations = useMemo(() => {
    const byDescription = stores.map((s) => normalizeExcel(s.description));
    const byCode = stores.map((s) => normalizeExcel(s.code));
    return Array.from(new Set([...byDescription, ...byCode])).filter(Boolean);
  }, [stores]);

  const locationSchema: Record<string, ExcelCellProps> = {
    Codigo: { type: "string", required: true },
    Almacen: {
      type: "string",
      required: true,
      associations: storeAssociations,
    },
    Tipo: {
      type: "string",
      required: true,
      associations: Object.values(LocationTypes).map((t) => normalizeExcel(t)),
    },
    Ancho: { type: "number", required: true },
    Alto: { type: "number", required: true },
    Largo: { type: "number", required: true },
  };

  function resolveStoreId(excelStoreValue: any) {
    const raw = normalizeExcel(excelStoreValue);

    const storeByCode = stores.find((s) => normalizeExcel(s.code) === raw);
    return storeByCode?.id ?? null;
  }

  function resolveLocationType(excelTypeValue: any) {
    const raw = normalizeExcel(excelTypeValue);
    const allowed = Object.values(LocationTypes);

    const match = allowed.find((t) => normalizeExcel(t) === raw);
    return match ?? null;
  }

  async function createLocationStructure(rows: NewLocationRow[]) {
    const errors: string[] = [];
    const transformed: NewLocationPayload[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const line = i + 1;

      const code = String(row["Codigo"] ?? "").trim();
      if (!code) {
        errors.push(`Fila ${line}: "Codigo" es requerido.`);
        continue;
      }

      const storeId = resolveStoreId(row["Almacen"]);
      if (!storeId) {
        errors.push(
          `Fila ${line}: Almacén "${row["Almacen"]}" no encontrado (por código o descripción).`,
        );
        continue;
      }

      const locationType = resolveLocationType(row["Tipo"]);
      if (!locationType) {
        errors.push(
          `Fila ${line}: Tipo "${row["Tipo"]}" no es válido. Valores: ${Object.values(LocationTypes).join(", ")}.`,
        );
        continue;
      }

      const width = toPositiveNumber(row["Ancho"]);
      const height = toPositiveNumber(row["Alto"]);
      const long = toPositiveNumber(row["Largo"]);

      if (!Number.isFinite(width) || width <= 0) {
        errors.push(`Fila ${line}: "Ancho" debe ser un número > 0.`);
        continue;
      }
      if (!Number.isFinite(height) || height <= 0) {
        errors.push(`Fila ${line}: "Alto" debe ser un número > 0.`);
        continue;
      }
      if (!Number.isFinite(long) || long <= 0) {
        errors.push(`Fila ${line}: "Largo" debe ser un número > 0.`);
        continue;
      }

      transformed.push({
        code,
        store_id: storeId,
        location_type: locationType,
        width,
        height,
        long,
      });
    }

    return { transformed, errors };
  }

  const inputMimeType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";

  const readFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    setError(false);
    setListOfErrors([]);
    e.preventDefault();

    try {
      const file = e.target.files?.[0];
      if (!file) {
        setError(true);
        setLoading(false);
        return;
      }

      const reader = new FileReader();

      reader.onload = async (ev) => {
        try {
          const workbook = read(ev.target?.result as ArrayBuffer, {
            type: "array",
          });

          const desired = SHEET_NAME.trim().toUpperCase();
          const sheetName = workbook.SheetNames.find(
            (n) => n.trim().toUpperCase() === desired,
          );

          if (!sheetName) {
            setError(true);
            setListOfErrors((prev) => [
              ...prev,
              `No se encontró la hoja "${SHEET_NAME}".`,
              `Hojas disponibles: ${workbook.SheetNames.join(", ")}.`,
            ]);
            setModalOpen(true);
            return;
          }

          const rawRows = utils.sheet_to_json(workbook.Sheets[sheetName], {
            defval: "",
          }) as NewLocationRow[];

          if (!rawRows.length) {
            setError(true);
            setListOfErrors([
              `La hoja "${sheetName}" no contiene filas para importar.`,
            ]);
            setModalOpen(true);
            return;
          }

          const CANONICAL_MAP: Record<string, string> = {
            codigo: "Codigo",
            almacen: "Almacen",
            tipo: "Tipo",
            ancho: "Ancho",
            alto: "Alto",
            largo: "Largo",
          };

          const canonicalRows = rawRows.map((row) => {
            const out: Record<string, any> = {};
            for (const key in row) {
              const nk = normalizeHeader(key);
              const target = CANONICAL_MAP[nk];
              if (target) out[target] = row[key];
            }
            return out as NewLocationRow;
          });

          const normalizedSchema = normalizeSchemaKeys(locationSchema);

          const normalizedRowsForValidation = canonicalRows.map((row) => {
            const r: Record<string, any> = {};
            for (const key in row) {
              r[normalizeHeader(key)] = row[key];
            }
            return r;
          });

          const result = validateSchema(
            normalizedRowsForValidation,
            normalizedSchema,
          );

          if (!result.isValid) {
            setListOfErrors(
              result.errors.map((err) => `${SHEET_NAME}: ${err.message}`),
            );
            setError(true);
            setModalOpen(true);
            return;
          }

          const { transformed, errors } =
            await createLocationStructure(canonicalRows);

          if (errors.length > 0) {
            setListOfErrors(errors);
            setError(true);
          } else {
            setUploadedLocations(transformed);
          }

          setModalOpen(true);
        } catch (ex: any) {
          setError(true);
          setListOfErrors((prev) => [
            ...prev,
            `Error procesando archivo: ${ex?.message}`,
          ]);
          setModalOpen(true);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (ex: any) {
      setError(true);
      setListOfErrors((prev) => [...prev, ex.message]);
      setLoading(false);
      setModalOpen(true);
    }
  };

  async function handleSubmit() {
    let hasBackendErrors = false;

    try {
      setLoading(true);
      setModalOpen(false);

      const resp = await createOrUpdateLocationsMassive(uploadedLocations);

      if (resp.status === STATUS_CODES.OK) {
        const { created, updated, failed, errors } = resp.data;

        if (created.length > 0) {
          for (const l of created) {
            addSessionData(ENTITY.LOCATIONS, l);
          }
        }
        if (updated.length > 0) {
          for (const l of updated) {
            updateSessionData(ENTITY.LOCATIONS, l);
          }
        }

        if (failed.length === 0) {
          showSnackBar(
            `Se crearon ${created.length} ubicaciones y se actualizaron ${updated.length} ubicaciones correctamente.`,
            "success",
          );
        } else {
          hasBackendErrors = true;
          setListOfErrors(errors ?? []);
          setError(true);
          setModalOpen(true);
          showSnackBar(
            `Resultado: ${created.length} creadas — ${updated.length} actualizadas — ${failed.length} con errores.`,
            "warning",
          );
          return;
        }
      } else {
        showSnackBar(resp.data?.message ?? "Error inesperado", "error");
      }
    } catch (ex: unknown) {
      const message =
        ex instanceof Error
          ? ex.message
          : typeof ex === "string"
            ? ex
            : "Error inesperado.";
      showSnackBar(message, "error");
    } finally {
      setLoading(false);

      if (!hasBackendErrors) {
        setModalOpen(false);
        setResetInput(Math.random().toString(36));
        setUploadedLocations([]);
      }
    }
  }

  return (
    <>
      <Button
        component="label"
        variant="contained"
        startIcon={<MdCloudUpload />}
      >
        {loading ? <CircularProgress size={20} /> : "Cargar Ubicaciones"}
        <VisuallyHiddenInput
          type="file"
          accept={inputMimeType}
          key={resetInput}
          ref={fileUploadRef}
          onChange={readFile}
        />
      </Button>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Paper
          sx={{
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            padding: "1rem",
            width: "500px",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Grid container rowGap={2}>
            {!error && uploadedLocations.length > 0 && (
              <>
                <Typography variant="h6">
                  Datos importados correctamente
                </Typography>
                <Typography>
                  Ubicaciones a importar{" "}
                  <span style={{ color: "green" }}>
                    {uploadedLocations.length}
                  </span>
                </Typography>

                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    startIcon={<MdCancel />}
                    onClick={() => {
                      setModalOpen(false);
                      setUploadedLocations([]);
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<MdCheck />}
                    onClick={handleSubmit}
                  >
                    Cargar Datos
                  </Button>
                </Grid>
              </>
            )}

            {error && (
              <>
                <Typography variant="h5" color="error" textAlign="center">
                  Ocurrió un error
                </Typography>

                <Grid container justifyContent="center" mt={2}>
                  <Grid item xs={12} md={10} lg={8}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                        Se encontraron ({listOfErrors.length}) errores
                      </AccordionSummary>
                      <AccordionDetails>
                        {listOfErrors.map((err, i) => (
                          <Alert key={i} severity="error" sx={{ mb: 1 }}>
                            {err}
                          </Alert>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Modal>

      <Backdrop
        open={loading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          color: "#fff",
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
